import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ourClientSchema } from "../schemas/ourClientSchema";
import { clientService } from "../services/clientService";
import { toast } from "../../../services/toastService";
import GalleryManager from "../../../components/GalleryManager";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const OurClientForm = ({ getAsync, id, setId }) => {
  const isEditMode = Boolean(id);

  const initialForm = {
    name: "",
    slug: "",
    shortDescription: "",
    fullDescription: "",
    websiteUrl: "",
    displayOrder: "",
    isPublished: true,
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(ourClientSchema),
    defaultValues: initialForm,
  });

  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [logoError, setLogoError] = useState("");

  const [existingMainImageUrl, setExistingMainImageUrl] = useState(null);
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreviewUrl, setMainImagePreviewUrl] = useState(null);
  const [mainImageError, setMainImageError] = useState("");

  const [images, setImages] = useState([]);

  useEffect(() => {
    if (id > 0) {
      editAsync();
    } else {
      reset(initialForm);
      resetImageState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      if (mainImagePreviewUrl) URL.revokeObjectURL(mainImagePreviewUrl);
    };
  }, [logoPreviewUrl, mainImagePreviewUrl]);

  const editAsync = async () => {
    try {
      const result = await clientService.edit(id);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to load client");
        return;
      }
      reset({
        name: result.value.name,
        slug: result.value.slug,
        shortDescription: result.value.shortDescription || "",
        fullDescription: result.value.fullDescription || "",
        websiteUrl: result.value.websiteUrl || "",
        displayOrder: result.value.displayOrder,
        isPublished: result.value.isPublished,
      });
      setExistingLogoUrl(result.value.logoPath);
      setExistingMainImageUrl(result.value.mainImagePath);
      setImages(result.value.images || []);
      setLogoFile(null);
      setLogoPreviewUrl(null);
      setMainImageFile(null);
      setMainImagePreviewUrl(null);
    } catch (error) {
      toast.error("An error occurred while loading the client");
      console.log(error);
    }
  };

  const resetImageState = () => {
    setExistingLogoUrl(null);
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setLogoError("");
    setExistingMainImageUrl(null);
    setMainImageFile(null);
    setMainImagePreviewUrl(null);
    setMainImageError("");
    setImages([]);
  };

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) return "Please choose a JPG, PNG, or WEBP image";
    if (file.size > MAX_FILE_SIZE) return "Image must not exceed 5 MB";
    return null;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const error = validateFile(file);
    setLogoError(error || "");
    if (error) return;

    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const error = validateFile(file);
    setMainImageError(error || "");
    if (error) return;

    if (mainImagePreviewUrl) URL.revokeObjectURL(mainImagePreviewUrl);
    setMainImageFile(file);
    setMainImagePreviewUrl(URL.createObjectURL(file));
  };

  const onSubmit = async (data) => {
    if (!isEditMode && !logoFile) {
      setLogoError("Logo is required");
      return;
    }

    const formData = new FormData();
    formData.append("Name", data.name);
    if (data.slug) formData.append("Slug", data.slug);
    if (data.shortDescription) formData.append("ShortDescription", data.shortDescription);
    if (data.fullDescription) formData.append("FullDescription", data.fullDescription);
    if (data.websiteUrl) formData.append("WebsiteUrl", data.websiteUrl);
    if (data.displayOrder !== null && data.displayOrder !== undefined && data.displayOrder !== "") {
      formData.append("DisplayOrder", data.displayOrder);
    }
    formData.append("IsPublished", data.isPublished ? "true" : "false");
    if (logoFile) formData.append("LogoFile", logoFile);
    if (mainImageFile) formData.append("MainImageFile", mainImageFile);

    if (isEditMode) await updateAsync(formData);
    else await addAsync(formData);
  };

  const addAsync = async (formData) => {
    try {
      const result = await clientService.create(formData);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to create client");
        return;
      }
      toast.success("Client added successfully");
      handleClear();
      await getAsync();
    } catch (error) {
      toast.error("An error occurred while creating the client");
      console.log(error);
    }
  };

  const updateAsync = async (formData) => {
    try {
      const result = await clientService.update(id, formData);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to update client");
        return;
      }
      toast.success("Client updated successfully");
      await editAsync();
      await getAsync();
    } catch (error) {
      toast.error("An error occurred while updating the client");
      console.log(error);
    }
  };

  const handleClear = () => {
    setId(0);
    reset(initialForm);
    resetImageState();
  };

  const uploadGalleryImageAsync = async (file) => {
    const formData = new FormData();
    formData.append("ImageFile", file);
    const result = await clientService.uploadImage(id, formData);
    if (!result.isSuccess) {
      toast.error(result.errors[0] || "Failed to upload image");
      return;
    }
    setImages((prev) => [...prev, result.value]);
  };

  const deleteGalleryImageAsync = async (imageId) => {
    const result = await clientService.deleteImage(id, imageId);
    if (!result.isSuccess) {
      toast.error(result.errors[0] || "Failed to delete image");
      return;
    }
    setImages((prev) => prev.filter((i) => i.id !== imageId));
  };

  const moveGalleryImage = async (imageId, direction) => {
    const sorted = [...images].sort((a, b) => a.displayOrder - b.displayOrder);
    const index = sorted.findIndex((i) => i.id === imageId);
    const swapIndex = index + direction;
    if (index === -1 || swapIndex < 0 || swapIndex >= sorted.length) return;

    const current = sorted[index];
    const swapWith = sorted[swapIndex];

    const result = await clientService.reorderImages(id, [
      { id: current.id, displayOrder: swapWith.displayOrder },
      { id: swapWith.id, displayOrder: current.displayOrder },
    ]);
    if (!result.isSuccess) {
      toast.error(result.errors[0] || "Failed to reorder images");
      return;
    }
    setImages((prev) =>
      prev.map((i) => {
        if (i.id === current.id) return { ...i, displayOrder: swapWith.displayOrder };
        if (i.id === swapWith.id) return { ...i, displayOrder: current.displayOrder };
        return i;
      })
    );
  };

  return (
    <section className="section-id-card-form">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Name</label>
              <input type="text" className="form-control" placeholder="Enter Name" {...register("name")} />
              <small className="text-danger">{errors.name?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Slug</label>
              <input type="text" className="form-control" placeholder="Leave blank to auto-generate" {...register("slug")} />
              <small className="text-danger">{errors.slug?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Website URL</label>
              <input type="text" className="form-control" placeholder="https://example.com" {...register("websiteUrl")} />
              <small className="text-danger">{errors.websiteUrl?.message}</small>
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label>Display Order</label>
              <input type="number" className="form-control" placeholder="Leave blank to add at the end" {...register("displayOrder")} />
              <small className="text-danger">{errors.displayOrder?.message}</small>
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label className="d-block">Published</label>
              <div className="form-check form-switch">
                <input type="checkbox" className="form-check-input" role="switch" {...register("isPublished")} />
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="form-group">
              <label>Short Description</label>
              <textarea className="form-control" rows={2} {...register("shortDescription")} />
              <small className="text-danger">{errors.shortDescription?.message}</small>
            </div>
          </div>

          <div className="col-12">
            <div className="form-group">
              <label>Full Description</label>
              <textarea className="form-control" rows={4} {...register("fullDescription")} />
              <small className="text-danger">{errors.fullDescription?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Logo</label>
              <input type="file" className="form-control" accept="image/jpeg,image/png,image/webp" onChange={handleLogoChange} />
              <small className="text-danger">{logoError}</small>
              {(logoPreviewUrl || existingLogoUrl) && (
                <div className="mt-2" style={{ width: 120, height: 90, background: "#f5f5f5", border: "1px solid #ddd" }}>
                  <img
                    src={logoPreviewUrl || existingLogoUrl}
                    alt="Logo"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Main Image (optional)</label>
              <input type="file" className="form-control" accept="image/jpeg,image/png,image/webp" onChange={handleMainImageChange} />
              <small className="text-danger">{mainImageError}</small>
              {(mainImagePreviewUrl || existingMainImageUrl) && (
                <div className="mt-2" style={{ width: 160, height: 90 }}>
                  <img
                    src={mainImagePreviewUrl || existingMainImageUrl}
                    alt="Main"
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-group">
              <label>Gallery Images</label>
              {isEditMode ? (
                <GalleryManager
                  images={images}
                  onUpload={uploadGalleryImageAsync}
                  onDelete={deleteGalleryImageAsync}
                  onMoveUp={(imageId) => moveGalleryImage(imageId, -1)}
                  onMoveDown={(imageId) => moveGalleryImage(imageId, 1)}
                />
              ) : (
                <small className="text-muted d-block">Save the client first to manage gallery images.</small>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-group flex-end">
              <button type="button" className="my-button bg-secondary mx-2" onClick={() => handleClear()}>
                Cancel
              </button>
              <button type="submit" className="my-button" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};

export default OurClientForm;
